import React from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import type { SentimentTagFormValue } from '@/lib/sentiment';

type SentimentTagsListProps = {
  tags: SentimentTagFormValue[];
  arrayError?: string;
  onAddTag: () => void;
  onEditTag: (tag: SentimentTagFormValue) => void;
  onRemoveTag: (tagId: string) => void;
};

export const SentimentTagsList = React.memo(
  ({ tags, arrayError, onAddTag, onEditTag, onRemoveTag }: SentimentTagsListProps) => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <Label>Sentiment tags</Label>
            <p className="text-sm text-muted-foreground">
              Define the labels the model can assign to a call. Tailor them to your use case.
            </p>
          </div>
          <Button variant="outline" type="button" onClick={onAddTag}>
            <Plus className="h-4 w-4" />
            Add tag
          </Button>
        </div>

        {tags.length > 0 ? (
          <div className="space-y-2">
            {tags.map(tag => (
              <div
                key={tag.id}
                className="flex flex-col gap-3 rounded-lg border px-3 py-3 transition-colors hover:bg-muted/50 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="space-y-1.5">
                  <span className="text-sm font-medium">{tag.label}</span>
                  {tag.description && (
                    <p className="text-sm text-muted-foreground">{tag.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-1 self-end sm:self-start">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    type="button"
                    onClick={() => onEditTag(tag)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    type="button"
                    className="hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => onRemoveTag(tag.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed px-4 py-5 text-sm text-muted-foreground">
            No sentiment tags added yet. Add at least one tag for the model to classify against.
          </div>
        )}

        {arrayError && <p className="text-sm text-destructive">{arrayError}</p>}
      </div>
    );
  }
);
